import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import event8 from "../../../images/community/nomadroof-events-8.png";
import event2 from "../../../images/community/nomadroof-events-2.png";
import event7 from "../../../images/community/nomadroof-events-7.png";

interface Highlight {
  title: string;
  desc: string;
  image: string;
}

interface CommunityHighlightsProps {
  highlights?: Highlight[];
}

const defaultHighlights: Highlight[] = [
  {
    title: "Trips & Adventures",
    desc: "From trekking into the mountains to exploring the Amazon river, we organize unforgettable trips across Peru.",
    image: event8,
  },
  {
    title: "Events & Socials",
    desc: "Weekly events, welcome parties, social meet-ups, and game nights to help students bond and make lifelong friends.",
    image: event2,
  },
  {
    title: "Student Support",
    desc: "Arrival tips, travel tips, cultural and local guidance — we're with you from check-in to farewell.",
    image: event7,
  },
];

const CommunityHighlights: React.FC<CommunityHighlightsProps> = ({
  highlights = defaultHighlights,
}) => {
  return (
    <section className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-3xl overflow-hidden shadow-2xl">

        {/* ── LEFT: Experiences CTA — 2/5 width on desktop ── */}
        <div className="relative overflow-hidden lg:col-span-2 min-h-[280px] lg:min-h-0">
          <img
            src="https://res.cloudinary.com/delmdmex1/image/upload/v1773335778/huacachina-pisco-10-media-1_i3ddlr.jpg"
            alt="Peru group adventures"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/55 to-[#f5694b]/30" />
          <div className="absolute bottom-0 right-0 w-36 h-36 bg-[#f5694b]/20 rounded-tl-full" />

          <div className="relative z-10 flex flex-col justify-between h-full p-6 lg:p-8">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#f5694b] text-white text-[11px] font-bold uppercase tracking-widest rounded-full mb-4">
                ✦ New
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-white leading-tight mb-3">
                Group Adventures
                <br />
                <span className="text-[#f5694b]">Across Peru</span>
              </h2>
              <p className="text-white/75 text-xs lg:text-sm leading-relaxed">
                Trek to Laguna 69, explore Huaraz, and travel with a community
                of students and young professionals.
              </p>
            </div>

            <Link
              to="/experiences"
              className="mt-6 self-start inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 hover:bg-[#f5694b] hover:text-white font-semibold rounded-full shadow-lg transition-all duration-300 hover:scale-105 text-xs lg:text-sm"
            >
              Explore Experiences
              <ArrowRightIcon className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* ── RIGHT: Community Highlights — 3/5 width on desktop ── */}
        <div className="bg-white dark:bg-neutral-900 flex flex-col lg:col-span-3">
          <div className="px-6 lg:px-8 pt-6 lg:pt-8 pb-4 border-b border-gray-100 dark:border-neutral-800">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              Community <span className="text-[#f5694b]">Highlights</span>
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Life beyond your accommodation
            </p>
          </div>

          <div className="flex flex-col divide-y divide-gray-100 dark:divide-neutral-800 flex-1">
            {highlights.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-4 px-6 lg:px-8 py-4 hover:bg-orange-50 dark:hover:bg-neutral-800 transition-colors duration-200 group"
              >
                {/* Thumbnail — square and prominent */}
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#f5694b] flex-shrink-0" />
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
                    {item.desc}
                  </p>
                </div>

                <ArrowRightIcon className="w-4 h-4 text-gray-300 dark:text-neutral-600 group-hover:text-[#f5694b] group-hover:translate-x-1 transition-all duration-200 flex-shrink-0" />
              </div>
            ))}
          </div>

          <div className="px-6 lg:px-8 py-4 border-t border-gray-100 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-800/50">
            <Link
              to="/experiences"
              className="text-sm font-semibold text-[#f5694b] hover:text-[#e2583d] inline-flex items-center gap-1.5 transition-colors"
            >
              See all upcoming events
              <ArrowRightIcon className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CommunityHighlights;